import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import useOfferList from 'hooks/Offers/useOfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import {
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import { EmptyObject } from 'lib/object/objectUtils'
import OfferListCarousel from '../OfferListCarousel'
import HeroOfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/HeroOfferCarouselLookingForSomethingElse'
import PaletteSheet from 'LuxKit/PaletteSheet'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import {
  isTourCampaignActive,
  TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
} from 'constants/tours'
import SiteTakeoverBackgroundImage from 'takeovers/utils/components/SiteTakeoverBackgroundImage'
import FilterChip from 'LuxKit/Chips/FilterChip'
import Places from 'constants/places'
import { useLocalizedPlaces } from 'hooks/usePlace'
import CarouselViewAllLink from 'components/Common/CarouselViewAllLink'
import { take } from 'lib/array/arrayUtils'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import AgentHubEarnYourOwnTourBanner from 'agentHub/components/AgentHubEarnYourOwnTourBanner'
import TourOfferCampaignBanner from 'components/Tours/TourOfferCampaignBanner'
import Sheet from 'components/utils/Sheet'
import clsx from 'clsx'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import {
  getExclusiveOfferCarouselSurface,
  hasCustomHeroOfferCarouselSurface,
} from 'home/components/OfferCarousels/heroOfferCarouselSurface'

interface Props {
  filters?: App.OfferListFilters
  random?: boolean
  onOffersShow?: (offerIds: Array<string>) => void
}

const SiteTakeoverBackground = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
`

const Content = styled(Group)`
  position: relative;
  z-index: 1;
`

const ExclusiveOfferContainer = styled(PaletteSheet)`
  position: relative;
  padding: ${rem(64)} 0 ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    padding: ${rem(88)} 0 ${rem(40)};
  }

  /*
   * Teal = Highlights / Primary / DarkBackground.
   * Dual gate must share useRebrandVersion() with ThemeProvider / body.app-palette-rebrand
   * (see HotelExclusiveOfferCarousel) so OPTIMIZELY_* env override stays in sync.
   * Prefer &&.class over nested && under body… (nested && emits a broken selector).
   */
  body.${REBRAND_PALETTE_CLASS} &&.rebrand-highlight {
    background-color: var(--palette-highlight-primary-dark-background);
  }
`

const TopExclusiveHeaderGrid = styled(PaletteSheet)`
  display: grid;
  align-items: center;
  grid-template:
    "title link"
    / minmax(0, 1fr) max-content;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    display: flex;
    gap: ${rem(16)};
  }

  > .title-area {
    grid-area: title;

    ${mediaQueryUp.tablet} {
      flex: 1;
      min-width: 0;
    }
  }

  > .link-area {
    grid-area: link;
  }

  > .filters-area {
    grid-column: 1 / -1;
    grid-row: 2;
  }
`

export function getLuxExclusiveToursSearchUrl(): string {
  const urlSearchParams = new URLSearchParams({
    destinationId: ANYWHERE_PLACE_ID,
    destinationName: ANYWHERE_NAME,
    categories: 'lux_exclusive',
  })

  return `/search/tours?${urlSearchParams.toString()}`
}

function TourHeroOfferCarousel(props: Props) {
  const intl = useIntl()
  const viewAllLabel = intl.formatMessage({
    id: 'qywu0sy_1NVQHCWxPYIf',
    defaultMessage: 'View all',
    description: 'Button text to view all available items in the current category or listing section',
  })
  const {
    filters = EmptyObject,
    random,
    onOffersShow,
  } = props

  const currentSiteTakeover = useCurrentSiteTakeover({ heroVertical: 'tours' })
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>()
  const isTourCampaignEnabled = isTourCampaignActive(region, config.BRAND)

  const carouselFilters = useMemo(() => {
    if (currentSiteTakeover?.id === 'tourbestasia') {
      return {
        ...filters,
        destinations: selectedFilter ? [selectedFilter] : TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
      }
    }

    return filters
  }, [filters, currentSiteTakeover, selectedFilter])

  const asiaTakeoverPlaceIds = useMemo(
    () => [Places.Asia.id, Places.Vietnam.id, Places.Japan.id, Places.SriLanka.id],
    [],
  )
  const localizedAsiaTakeoverPlaces = useLocalizedPlaces(asiaTakeoverPlaceIds)

  const tourSearchURls = useMemo(() => {
    let urlSearchParams
    if (currentSiteTakeover?.id === 'tourbestasia') {
      const { destinations, operators } = carouselFilters
      const asiaDestinationName = localizedAsiaTakeoverPlaces[Places.Asia.id]?.place?.name
      if (!asiaDestinationName) {
        return getLuxExclusiveToursSearchUrl()
      }
      urlSearchParams = new URLSearchParams({
        destinationId: Places.Asia.id,
        destinationName: asiaDestinationName,
        destinations,
        operators,
      })
    } else {
      return getLuxExclusiveToursSearchUrl()
    }
    return `/search/tours?${urlSearchParams.toString()}`
  }, [carouselFilters, currentSiteTakeover, localizedAsiaTakeoverPlaces])

  const filterOptions = useMemo<Array<{ label: string, value: string }>>(() => {
    if (currentSiteTakeover?.id === 'tourbestasia') {
      return [
        { placeId: Places.Vietnam.id, value: Places.Vietnam.id },
        { placeId: Places.Japan.id, value: Places.Japan.id },
        { placeId: Places.SriLanka.id, value: Places.SriLanka.id },
      ].flatMap(({ placeId, value }) => {
        const label = localizedAsiaTakeoverPlaces[placeId]?.place?.name
        return label ? [{ label, value }] : []
      })
    }
    return []
  }, [currentSiteTakeover, localizedAsiaTakeoverPlaces])

  const toggleFilter = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const filter = e.currentTarget.dataset.value!
    const nextFilter = selectedFilter === filter ? undefined : filter
    setSelectedFilter(nextFilter)
  }, [selectedFilter])

  const offerList = useOfferList(carouselFilters, { randomize: random })
  const tracking = useOfferListTracking(getListName('OfferList', 'TOURS', 'Hero Carousel'), { key: getOfferListKey(filters) })

  const hasInitOffersLoaded = useRef(false)
  useEffect(() => {
    if (!hasInitOffersLoaded.current && !offerList.fetching && !!offerList.offerIds.length) {
      hasInitOffersLoaded.current = true
      onOffersShow?.(take(offerList.offerIds, 3))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerList])

  const paletteType = currentSiteTakeover?.palette ?? 'inverse'
  const headerPaletteType = currentSiteTakeover?.heroSection?.palette ?? 'inverse'
  // A takeover owns the surface (background image + custom palette); skip default rebrand teal.
  const hasCustomHeroSurface = hasCustomHeroOfferCarouselSurface({
    siteTakeoverTheme: currentSiteTakeover,
    heroCarouselConfig: currentSiteTakeover?.heroSection,
  })
  const {
    isRebrandExclusiveSurface,
    containerPalette,
    titleSheetPalette,
    carouselSheetPalette,
    tileStyle,
  } = getExclusiveOfferCarouselSurface({
    isRebrand: useRebrandVersion() === 'rebrand',
    hasCustomHeroSurface,
    palette: paletteType,
  })
  const headerSheetPalette = isRebrandExclusiveSurface ? titleSheetPalette : headerPaletteType

  return (
    <ExclusiveOfferContainer
      paletteType={containerPalette}
      className={clsx({ 'rebrand-highlight': isRebrandExclusiveSurface })}
    >
      <AgentHubEarnYourOwnTourBanner />
      {currentSiteTakeover && <SiteTakeoverBackground>
        <SiteTakeoverBackgroundImage background="hero" vertical="tours" />
      </SiteTakeoverBackground>}
      <Content direction="vertical" gap={24} tabletGap={32}>
        {isTourCampaignEnabled && <LayoutContainer>
          <TourOfferCampaignBanner />
        </LayoutContainer>}
        <LayoutContainer>
          <TopExclusiveHeaderGrid paletteType={headerSheetPalette}>
            <Group className="title-area" direction="horizontal" gap={12}>
              {currentSiteTakeover?.heroSection?.logo}
              <Group direction="vertical" verticalAlign="center">
                {!currentSiteTakeover?.heroSection?.title && <Heading className="title-area" variant="heading2" align="start">
                  <FormattedMessage
                    id="l3wbcUEHDJMUgG_szQyX"
                    defaultMessage="Today’s top <i>exclusive</i> offers"
                    description="Top picks for me carousel title with italic 'exclusive'"
                    values={{
                      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                    }}
                  />
                </Heading>}
                {currentSiteTakeover?.heroSection?.title}
                {currentSiteTakeover?.heroSection?.subtitle}
              </Group>
            </Group>
            {!!filterOptions.length && <PaletteSheet className="filters-area" paletteType="default">
              <Group direction="horizontal" gap={8}>
                {filterOptions.map(option =>
                  <FilterChip
                    key={option.value}
                    size="medium"
                    selected={option.value === selectedFilter}
                    data-value={option.value}
                    onClick={toggleFilter}
                  >
                    {option.label}
                  </FilterChip>,
                )}
              </Group>
            </PaletteSheet>}
            <CarouselViewAllLink className="link-area" to={tourSearchURls}>{viewAllLabel}</CarouselViewAllLink>
          </TopExclusiveHeaderGrid>
        </LayoutContainer>
        <Sheet paletteType={carouselSheetPalette} background="none">
          <OfferListEventsProvider tracking={tracking}>
            <OfferListCarousel
              tileStyle={tileStyle}
              tileSize="medium"
              overrideOfferList={offerList}
              limit={15}
            >
              <HeroOfferCarouselLookingForSomethingElse
                link={tourSearchURls}
                offerType="tours"
              />
            </OfferListCarousel>
          </OfferListEventsProvider>
        </Sheet>
      </Content>
    </ExclusiveOfferContainer>
  )
}

export default TourHeroOfferCarousel
